import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ROUTES,
  ROUTE_TYPES,
  RouteBuilders,
  parseRoute,
} from "../classes/constants/routeConstants";

export const useRouteState = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { patientId, mode, twinId, compareId, planId } = useParams();

  const lastValidParamsRef = useRef({});
  const navigationHistoryRef = useRef({
    previous: undefined,
    current: undefined,
    canGoBack: typeof window !== "undefined" && window.history.length > 1,
  });
  const previousRouteTypeRef = useRef(parseRoute(location.pathname).routeType);

  const routeInfo = useMemo(() => {
    const routeType = parseRoute(location.pathname).routeType;
    const previousRouteType = previousRouteTypeRef.current;
    previousRouteTypeRef.current = routeType;

    const currentParams = { patientId, mode, twinId, compareId, planId };
    const hasValidParams = Object.values(currentParams).some(
      param => param !== undefined
    );

    if (hasValidParams) {
      lastValidParamsRef.current = currentParams;
    }

    return {
      routeType,
      previousRouteType,
      params: hasValidParams ? currentParams : lastValidParamsRef.current,
    };
  }, [compareId, location.pathname, mode, patientId, planId, twinId]);

  const routeState = useMemo(
    () => ({
      routeType: routeInfo.routeType,
      previousRouteType: routeInfo.previousRouteType,
      pathname: location.pathname,
      params: routeInfo.params,
      isLogin: routeInfo.routeType === ROUTE_TYPES.LOGIN,
      isPatientContext: !!routeInfo.params.patientId,
      isTwinContext: !!routeInfo.params.twinId,
      isPlanContext: !!routeInfo.params.planId,
      hasCompare: !!routeInfo.params.compareId,
    }),
    [routeInfo, location.pathname]
  );

  const isUtilityRoute = routeType =>
    routeType === ROUTE_TYPES.SETTINGS || routeType === ROUTE_TYPES.SUPPORT;

  const navigateToSupport = () => {
    navigate(ROUTES.SUPPORT);
  };

  const navigateToHome = () => {
    navigate(ROUTES.HOME);
  };

  const navigateToCheck = (patientId, twinId) => {
    navigate(RouteBuilders.check(patientId, twinId));
  };

  const navigateToAlign = (patientId, twinId, planId) => {
    navigate(RouteBuilders.align(patientId, twinId, planId));
  };

  const navigateToAlignComparison = (patientId, twinId, planId, comparePlanId) => {
    navigate(RouteBuilders.alignComparison(patientId, twinId, planId, comparePlanId));
  };

  const navigateToValidation = (patientId, twinId, validationMode = null) => {
    navigate(RouteBuilders.validation(patientId, twinId, validationMode));
  };

  const navigateToPatientDashboard = patientId => {
    navigate(RouteBuilders.patientDashboard(patientId));
  };

  const navigateToTwinSelection = patientId => {
    navigate(RouteBuilders.digitalTwinSelection(patientId));
  };

  const navigateToTwinSelected = (patientId, twinId, compareId = null) => {
    navigate(RouteBuilders.digitalTwinSelected(patientId, twinId, compareId));
  };

  const navigateToTwinCreation = patientId => {
    navigate(RouteBuilders.digitalTwinCreation(patientId));
  };

  const navigateToTwinComparison = (patientId, twinId, compareId) => {
    navigate(RouteBuilders.digitalTwinComparison(patientId, twinId, compareId));
  };

  const navigateToTwinEdit = (patientId, twinId) => {
    navigate(RouteBuilders.digitalTwinEdit(patientId, twinId));
  };

  const isRouteActive = targetRouteType => {
    return routeInfo.routeType === targetRouteType;
  };

  const routeHasParameter = paramName => {
    const isNotUndefined = paramName in routeInfo.params;
    const isNotEmpty = !!routeInfo.params[paramName];
    return isNotUndefined && isNotEmpty;
  };

  const getParameter = paramName => {
    return routeInfo.params[paramName];
  };

  const getTwinId = () => {
    return routeInfo.params.twinId;
  };
  const getCompareId = () => {
    return routeInfo.params.compareId;
  };
  const getPlanId = () => {
    return routeInfo.params.planId;
  };
  const getPatientId = () => {
    return routeInfo.params.patientId;
  };
  const getMode = () => {
    return routeInfo.params.mode;
  };

  const goBack = () => {
    const { previous, current } = navigationHistoryRef.current;
    const browserCanGoBack =
      typeof window !== "undefined" && window.history.length > 1;

    if (browserCanGoBack) {
      navigate(-1);
      return;
    }

    if (previous && previous !== current) {
      navigate(previous);
      return;
    }

    navigate(ROUTES.HOME);
  };

  useEffect(() => {
    if (!isUtilityRoute(routeInfo.routeType)) {
      navigationHistoryRef.current = {
        previous: navigationHistoryRef.current.current,
        current: location.pathname,
        canGoBack: typeof window !== "undefined" && window.history.length > 1,
      };
    }
  }, [location.pathname, routeInfo.routeType]);

  return {
    ...routeState,
    navigateToSupport,
    navigateToHome,
    navigateToValidation,
    navigateToCheck,
    navigateToAlign,
    navigateToAlignComparison,
    navigateToPatientDashboard,
    navigateToTwinSelection,
    navigateToTwinSelected,
    navigateToTwinCreation,
    navigateToTwinComparison,
    navigateToTwinEdit,
    getParameter,
    getTwinId,
    getCompareId,
    getPlanId,
    getPatientId,
    getMode,
    isRouteActive,
    routeHasParameter,
    goBack,
    navigationHistory: navigationHistoryRef.current,
    isUtilityRoute: isUtilityRoute(routeInfo.routeType),
  };
};
